/* eslint-disable react/prop-types */
/* eslint-disable */
import React, { useState, useEffect } from 'react'
import * as gdeTransactions from '../../../services/firestore/gdeTransactions'
import { makeStyles } from '@material-ui/core/styles'
import {
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core'
import { Delete, Add, Restore, Edit as EditIcon } from '@material-ui/icons'
import { MaterialReactTable } from 'material-react-table'
import { useTranslation } from 'react-i18next'
import { getSlideoutInfo, getUser } from '../../../redux/global'
import { successToast, errorToast } from '../../../common/functions'
import ExportCsv from '../../Controls/ExportCsv'
import AddCustomerModal from './AddCustomerModal'

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
  },
  fieldSelection: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

const ManageCustExclusionsFields = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [customers, setCustomers] = useState([])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteIndex, setDeleteIndex] = useState(null)
  const [loading, setLoading] = useState(true)
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false)
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false)
  const [editIndex, setEditIndex] = useState(null)
  const [hasPermission, setHasPermission] = useState(false)

  const { userName } = getSlideoutInfo()
  const { workerId } = getUser()
  const userNameWithId = `${workerId} (${userName})`

  useEffect(() => {
    if (workerId) {
      checkManageExclusionsPermission()
    }
    const unsubscribe = gdeTransactions.getfederalCustomersForExclusionListener('listener', (snapshot) => {
      try {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          accountLevel1: doc.data().accountLevel1 || doc.data().customerId || doc.id,
          accountLevel2: doc.data().accountLevel2 || '',
          accountLevel3: doc.data().accountLevel3 || '',
          createdTS: doc.data().createdTS || '',
          updatedTS: doc.data().updatedTS || '',
          wmsInstance: doc.data().wmsInstance || '',
          wmsRegion: doc.data().wmsRegion || '',
          isSC: doc.data().isSC ?? false,
          isEdge: doc.data().isEdge ?? false,
          offshoreExcl: doc.data().offshoreExcl ?? false,
          exception: doc.data().exception || '-',
          updatedBy: doc.data().updatedBy || '',
          isActive: doc.data().isActive ?? true,
        }))
        const sorted = data.sort((a, b) => {
          const dateA = new Date(a.updatedTS || a.createdTS || 0)
          const dateB = new Date(b.updatedTS || b.createdTS || 0)
          return dateB - dateA
        })
        setCustomers(sorted)
        setLoading(false)
      } catch (error) {
        console.error('Error in listener:', error)
        setLoading(false)
      }
    })
    return () => unsubscribe()
  }, [workerId])

  useEffect(() => {
    return () => {
      setConfirmDelete(false)
      setAddCustomerModalOpen(false)
      setDeleteIndex(null)
      setCustomers([])
      setLoading(true)
    }
  }, [])

  const checkManageExclusionsPermission = () => {
    try {
      const additionalGivenPermissions = window.sessionStorage.getItem('additional-permissions-user')
      if (!additionalGivenPermissions || additionalGivenPermissions === 'undefined') {
        setHasPermission(false)
        return
      }
      const additionalPermissionsForUser = JSON.parse(additionalGivenPermissions)
      const idx = additionalPermissionsForUser.map((object) => object.value).indexOf('manageExclusions')
      setHasPermission(idx >= 0 && !!additionalPermissionsForUser[idx]?.enabled)
    } catch (error) {
      console.error('Error checking manageExclusions permission:', error)
      setHasPermission(false)
    }
  }

  const handleEditClick = (index) => {
    const customer = customers[index]
    if (customer) {
      setEditIndex(index)
      setEditCustomerModalOpen(true)
    }
  }

  const handleDeleteConfirmation = (index) => {
    setDeleteIndex(index)
    setConfirmDelete(true)
  }

  const handleDeleteRow = async () => {
    try {
      if (deleteIndex == null) {
        setConfirmDelete(false)
        return
      }
      const updated = [...customers]
      const target = updated[deleteIndex]
      if (!target) {
        setConfirmDelete(false)
        return
      }

      // Store the original state before updating
      const originalIsActive = target.isActive
      const newIsActive = !target.isActive
      
      // Close the dialog immediately to prevent UI flash
      setConfirmDelete(false)
      setDeleteIndex(null)

      updated[deleteIndex] = {
        ...target,
        isActive: newIsActive,
        updatedTS: new Date().toISOString(),
        updatedBy: userNameWithId,
      }
      setCustomers(updated)
      
      if (target.id) {
        const payload = {
          accountLevel1: updated[deleteIndex].accountLevel1,
          accountLevel2: updated[deleteIndex].accountLevel2,
          accountLevel3: updated[deleteIndex].accountLevel3,
          updatedTS: updated[deleteIndex].updatedTS,
          updatedBy: updated[deleteIndex].updatedBy,
          isActive: updated[deleteIndex].isActive,
        }
        await gdeTransactions.updateFederalCustomerExclusion(
          target.id,
          payload
        )
      } else {
        if (!target.id && !newIsActive) {
          const filtered = updated.filter((_, idx) => idx !== deleteIndex)
          setCustomers(filtered)
        }
      }

      const messageKey = originalIsActive
        ? t('toast.customerFieldsDeleted')
        : t('toast.customerFieldsRestored')
      successToast({
        message: messageKey,
        title: t('toast.success'),
        autoHideDuration: 3000,
        isBlocking: false,
      })
    } catch (error) {
      console.error('Error toggling active state:', error)
      errorToast({
        message: t('toast.saveRowError'),
        title: t('toast.error'),
        autoHideDuration: 3000,
        isBlocking: false,
      })
    }
  }

  const handleCancelDelete = () => {
    setConfirmDelete(false)
    setDeleteIndex(null)
  }

  const handleAddCustomer = async (customerData) => {
    try {
      const duplicate = customers.some((c) =>
        c.accountLevel1 === customerData.accountLevel1 &&
        c.accountLevel2 === customerData.accountLevel2 &&
        c.accountLevel3 === customerData.accountLevel3
      )
      if (duplicate) {
        errorToast({
          message: t('dataEntry.duplicateValue'),
          title: t('toast.error'),
        })
        return
      }

      const dataToCreate = {
        accountLevel1: customerData.accountLevel1,
        accountLevel2: customerData.accountLevel2,
        accountLevel3: customerData.accountLevel3,
        wmsInstance: customerData.wmsInstance,
        wmsRegion: customerData.wmsRegion,
        isEdge: (customerData.exclusionType || []).includes('Local AI'),
        isSC: (customerData.exclusionType || []).includes('Smart Catalog'),
        offshoreExcl: (customerData.exclusionType || []).includes('Offshore Exclusion'),
        exception: customerData.exclusionReason === 'Other' ? customerData.otherReason : customerData.exclusionReason,
        createdTS: new Date().toISOString(),
        createdBy: userNameWithId,
        updatedTS: new Date().toISOString(),
        updatedBy: userNameWithId,
        isActive: true,
      }
      await gdeTransactions.createFederalCustomerExclusion(dataToCreate)
      successToast({
        message: t('toast.customerFieldsSaved'),
        title: t('toast.success'),
      })
    } catch (error) {
      console.error('Error adding customer:', error)
      errorToast({
        message: t('toast.saveRowError'),
        title: t('toast.error'),
      })
    }
  }

  const handleEditCustomer = async (customerData) => {
    try {
      if (editIndex == null) {
        setEditCustomerModalOpen(false)
        return
      }
      const updated = [...customers]
      const target = updated[editIndex]
      if (!target) {
        setEditCustomerModalOpen(false)
        return
      }

      updated[editIndex] = {
        ...target,
        wmsInstance: customerData.wmsInstance,
        wmsRegion: customerData.wmsRegion,
        isEdge: (customerData.exclusionType || []).includes('Local AI'),
        isSC: (customerData.exclusionType || []).includes('Smart Catalog'),
        offshoreExcl: (customerData.exclusionType || []).includes('Offshore Exclusion'),
        exception: customerData.exclusionReason === 'Other' ? customerData.otherReason : customerData.exclusionReason,
        updatedTS: new Date().toISOString(),
        updatedBy: userNameWithId,
      }
      setCustomers(updated)

      if (target.id) {
        const payload = {
          accountLevel1: updated[editIndex].accountLevel1,
          accountLevel2: updated[editIndex].accountLevel2,
          accountLevel3: updated[editIndex].accountLevel3,
          wmsInstance: updated[editIndex].wmsInstance,
          wmsRegion: updated[editIndex].wmsRegion,
          isSC: updated[editIndex].isSC,
          isEdge: updated[editIndex].isEdge,
          offshoreExcl: updated[editIndex].offshoreExcl,
          exception: updated[editIndex].exception,
          updatedTS: updated[editIndex].updatedTS,
          updatedBy: updated[editIndex].updatedBy,
          isActive: updated[editIndex].isActive,
        }
        await gdeTransactions.updateFederalCustomerExclusion(
          target.id,
          payload
        )
      }

      successToast({
        message: t('toast.customerFieldsUpdated'),
        title: t('toast.success'),
        autoHideDuration: 3000,
        isBlocking: false,
      })
    } catch (error) {
      console.error('Error updating customer:', error)
      errorToast({
        message: t('toast.saveRowError'),
        title: t('toast.error'),
        autoHideDuration: 3000,
        isBlocking: false,
      })
    } finally {
      setEditCustomerModalOpen(false)
      setEditIndex(null)
    }
  }

  const columns = [
    {
      accessorKey: 'accountLevel1',
      header: t('templates.accountLevelOne'),
      Cell: ({ row }) =>
        <Typography>{row.original.accountLevel1}</Typography>
    },
    {
      accessorKey: 'accountLevel2',
      header: t('templates.accountLevelTwo'),
      Cell: ({ row }) =>
        <Typography>{row.original.accountLevel2}</Typography>
    },
    {
      accessorKey: 'accountLevel3',
      header: t('templates.accountLevelThree'),
      Cell: ({ row }) =>
        <Typography>{row.original.accountLevel3}</Typography>
    },
    {
      accessorKey: 'isSC',
      header: t('tableColumns.smartCatalogExcluded'),
      Cell: ({ row }) => (
        <Typography>{row.original.isSC ? 'Yes' : 'No'}</Typography>
      ),
    },
    {
      accessorKey: 'isEdge',
      header: t('tableColumns.localAIExcluded'),
      Cell: ({ row }) => (
        <Typography>{row.original.isEdge ? 'Yes' : 'No'}</Typography>
      ),
    },
    {
      accessorKey: 'offshoreExcl',
      header: t('tableColumns.offshoreExcluded'),
      Cell: ({ row }) => (
        <Typography>{row.original.offshoreExcl ? 'Yes' : 'No'}</Typography>
      ),
    },
    {
      accessorKey: 'exception',
      header: t('tableColumns.exclusionReason'),
      Cell: ({ row }) => (
        <Typography>{row.original.exception || '-'}</Typography>
      ),
    },
    // {
    //   accessorKey: 'updatedBy',
    //   header: t('tableColumns.updatedBy'),
    //   Cell: ({ row }) => <Typography>{row.original.updatedBy}</Typography>,
    // },
    {
      accessorKey: 'isActive',
      header: t('tableColumns.isActive'),
      Cell: ({ row }) => (
        <Typography>{row.original.isActive ? 'Yes' : 'No'}</Typography>
      ),
    },
    {
      accessorKey: 'actions',
      header: t('tableColumns.action'),
      Cell: (cell) => {
        const dataIndex = cell.row.index
        const row = cell.row
        const isActive = row.original.isActive !== false

        return (
          <div className={classes.actions}>
            <>
              <IconButton
                onClick={() => handleEditClick(dataIndex)}
                color='primary'
                disabled={!hasPermission}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleDeleteConfirmation(dataIndex)}
                color='error'
                disabled={!hasPermission}
              >
                {isActive ? <Delete /> : <Restore />}
              </IconButton>
            </>
          </div>
        )
      },
    },
  ]

  return (
    <div className={classes.container}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Button variant='contained' color='primary' disabled={!hasPermission} onClick={() => setAddCustomerModalOpen(true)}>
          <Add style={{ marginRight: 8 }} />
          {t('dialogs.addNewCustomer')}
        </Button>
      </div>
      <MaterialReactTable
        columns={columns}
        data={customers}
        enableRowSelection
        enableColumnFilters
        muiTableHeadCellProps={{
          sx: {
            color: '#58595B',
            fontWeight: 'bold',
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: {
            backgroundColor:
              row?.original?.isActive === false ? '#E4f7ff' : 'white',
          },
        })}
        renderTopToolbarCustomActions={({ table }) => {
          const isDisabled = !table.getIsSomeRowsSelected()
          const isNoData = customers?.length === 0
          const isNoPageData = customers?.length > 4
          return (
            <ExportCsv
              columns={columns}
              isDisabled={isDisabled}
              isNoData={isNoData}
              isNoPageData={isNoPageData}
              table={table}
              hideExportSelectedData={false}
              hideExportPageData={false}
              enableExport={true}
            />
          )
        }}
        state={{ isLoading: loading }}
      />
      {/* Confirmation Dialog */}
      <Dialog open={confirmDelete} onClose={handleCancelDelete}>
        <DialogTitle>{t('templates.confirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteIndex != null && customers[deleteIndex]
              ? customers[deleteIndex].isActive
                ? t('toast.confirmInactiveMsg', { customerId: customers[deleteIndex].accountLevel1 })
                : t('toast.confirmActiveMsg')
              : t('templates.confirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color='default'>
            {t('buttons.no')}
          </Button>
          <Button
            onClick={handleDeleteRow}
            color={customers[deleteIndex]?.isActive ? 'primary' : 'secondary'}
            disabled={!hasPermission}
          >
            {t('buttons.yes')}
          </Button>
        </DialogActions>
      </Dialog>
      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={() => setAddCustomerModalOpen(false)}
        onAddCustomer={handleAddCustomer}
      />
      {editIndex != null && customers[editIndex] && (
        <AddCustomerModal
          open={editCustomerModalOpen}
          onClose={() => {
            setEditCustomerModalOpen(false)
            setEditIndex(null)
          }}
          onAddCustomer={handleEditCustomer}
          isEditMode={true}
          editData={customers[editIndex]}
        />
      )}
    </div>
  )
}

export default ManageCustExclusionsFields
